import React, { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

import LeftRail, { LeftRailProvider } from './LeftRail/LeftRail';
import RightPanel, { RightPanelProvider } from './RightPanel/RightPanel';
import ErrorBoundary from './ErrorBoundary';
import KeyboardShortcutsOverlay from './KeyboardShortcutsOverlay';
import AppSidebar, { SIDEBAR_COLLAPSED, SIDEBAR_WIDTH } from './shell/AppSidebar';
import AppTopBar from './shell/AppTopBar';
import ScreenContainer from './ui/ScreenContainer';
import ComparisonTray from './comparison/ComparisonTray';

const FULL_BLEED_ROUTES = ['/graph'];
const STORAGE_KEY = 'mvkg.sidebar.collapsed';

/**
 * Sidebar-first shell: AppSidebar + AppTopBar + contextual LeftRail + RightPanel.
 * Graph geometry/render unchanged; pages inject rail/panel via hooks.
 */
function LayoutInner({ children }) {
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === '1';
    } catch (_) {
      return false;
    }
  });

  const toggleSidebar = () => {
    setSidebarCollapsed((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch (_) { /* ignore */ }
      return next;
    });
  };

  const fullBleed = FULL_BLEED_ROUTES.some((r) => location.pathname.startsWith(r));
  const sidebarWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED : SIDEBAR_WIDTH;

  // Page-transition key uses only the TOP route segment, not the full pathname.
  // Otherwise navigating between sub-routes (e.g. /rag/:id1 → /rag/:id2 as the
  // Ask page syncs the active session into the URL) would remount the whole page
  // every time — which previously drove an infinite "new session" creation loop
  // and a perpetual fade/flicker. Sub-route changes now update in place.
  const routeKey = `/${location.pathname.split('/')[1] || ''}`;

  const screenMaxWidth = useMemo(() => {
    const p = location.pathname;
    if (p.startsWith('/rag') || p.startsWith('/discovery')) return 1500;
    // The paper reader is a three-pane workspace; its own views cap their width.
    if (p.startsWith('/digest')) return 2400;
    return 1400;
  }, [location.pathname]);

  const mainPadding = useMemo(() => {
    if (fullBleed) return 0;
    return { xs: 2, md: 3 };
  }, [fullBleed]);

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Keyboard users skip the sidebar and top bar in one step. */}
      <Box
        component="a" href="#main-content"
        sx={{
          position: 'absolute', left: 8, top: -48, zIndex: 2000, px: 1.5, py: 1, borderRadius: 2,
          bgcolor: 'background.paper', color: 'primary.main', fontWeight: 700, boxShadow: 3,
          '&:focus': { top: 8, outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        Skip to content
      </Box>
      <AppSidebar collapsed={sidebarCollapsed} onToggleCollapsed={toggleSidebar} />

      <Box
        sx={{
          flex: 1,
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          ml: 0,
          width: `calc(100% - ${sidebarWidth}px)`,
        }}
      >
        <AppTopBar sidebarCollapsed={sidebarCollapsed} />

        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'stretch',
            // No min-height: `flex: 1` already fills the column under the top
            // bar. `100vh - 72px` ignored the bar's 8px margin, so every page was
            // 8px taller than the window and scrolled a little for nothing.
          }}
        >
          <LeftRail route={location.pathname} />

          <Box
            component="main"
            id="main-content"
            tabIndex={-1}
            sx={{
              '&:focus': { outline: 'none' },
              flex: 1,
              minWidth: 0,
              p: mainPadding,
              // `clip`, not `hidden`: hidden makes <main> a scroll container, and
              // every `position: sticky` inside it (digest tabs, TOCs, rails)
              // then sticks to a box that never scrolls — i.e. never sticks.
              // The full-bleed graph keeps the original `hidden`: its canvas
              // layout was built against that box.
              overflowX: fullBleed ? 'hidden' : 'clip',
            }}
          >
            <AnimatePresence mode="wait">
              <motion.div
                key={routeKey}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -4 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                style={{ height: fullBleed ? '100%' : 'auto' }}
              >
                <ErrorBoundary resetKey={location.pathname}>
                  {fullBleed ? children : <ScreenContainer maxWidth={screenMaxWidth}>{children}</ScreenContainer>}
                </ErrorBoundary>
              </motion.div>
            </AnimatePresence>
          </Box>

          <RightPanel />
        </Box>
      </Box>
      {/* `?` keyboard shortcuts overlay — UX audit 2026-05-26 §1. */}
      <KeyboardShortcutsOverlay />
      {/* Floating claim-comparison tray → Claim Adjudicator (renders in /rag). */}
      <ComparisonTray />
    </Box>
  );
}

export default function Layout({ children }) {
  return (
    <LeftRailProvider>
      <RightPanelProvider>
        <LayoutInner>{children}</LayoutInner>
      </RightPanelProvider>
    </LeftRailProvider>
  );
}
